/**
 * Helpers for Daily Transaction Sheet WAC preview / apply.
 *
 * Backend FuelWacRecalculationService returns:
 *   stats.purchase_rows_with_changes
 *   stats.sales_rows_with_changes
 *
 * The legacy Blade sheet already reads those keys. The React panel must
 * use the same names so Apply sales / Apply purchase enable correctly.
 */

const SALE_STEP_TYPES = ['fuel_sale', 'recoup', 'sale', 'sales'];

function toCount(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function countSteps(steps, predicate) {
  return (steps || []).filter((step) => step && step.will_change && predicate(step)).length;
}

/**
 * @param {Record<string, unknown>} stats
 * @param {Array<Record<string, unknown>>} steps
 * @returns {{ purchase: number, sales: number }}
 */
export function countWacPreviewChanges(stats = {}, steps = []) {
  const purchaseFromStats = stats.purchase_rows_with_changes ?? stats.purchase_changes;
  const salesFromStats = stats.sales_rows_with_changes
    ?? stats.nozzle_rows_with_changes
    ?? stats.sales_changes;

  const purchase = purchaseFromStats != null
    ? toCount(purchaseFromStats)
    : countSteps(steps, (step) => step.type === 'purchase');

  const sales = salesFromStats != null
    ? toCount(salesFromStats)
    : countSteps(steps, (step) => SALE_STEP_TYPES.includes(step.type));

  return { purchase, sales };
}

export function wacTypeLabel(type) {
  if (type === 'purchase') {
    return 'Purchase (WAC)';
  }
  if (type === 'fuel_sale' || type === 'sale' || type === 'sales') {
    return 'Sale (COGS)';
  }
  if (type === 'recoup') {
    return 'Recoup (COGS)';
  }
  return type || '—';
}

export function wacStepProductLabel(step = {}) {
  const direct = step.product_name || step.fuel_name;
  if (direct) {
    return direct;
  }

  const tankLabel = String(step.tank || '');
  if (/^(petrol|diesel|ms|hsd)\b/i.test(tankLabel)) {
    return tankLabel.split(/[·\-]/)[0].trim();
  }

  return '—';
}

export function wacStepDetailLabel(step = {}) {
  return step.label || '—';
}

export function wacStepTankLabel(step = {}) {
  return step.tank || '—';
}

/** @deprecated Use wacStepProductLabel / wacStepDetailLabel / wacStepTankLabel */
export function wacStepLegacyDetailLabel(step = {}) {
  return step.tank || step.product_name || step.fuel_name || step.label || '—';
}
